import { formatRepoFiltersToApiFilters } from '@webapp/core/utils/repositories/formatRepoFiltersToApiFilters'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { type RepoFilterParams } from '@webapp/infrastructure/interfaces/repositories/RepoFilterParams'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import { type RepoStandardResponse } from '@webapp/infrastructure/interfaces/repositories/RepoStandardResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import { type ICardholderRepository } from '@webapp/modules/spending-cards/business-logic/interfaces/ICardholderRepository'
import { mapCardholderAccountDetailsApiModel } from '@webapp/modules/spending-cards/data-access/mappers/mapCardholderAccountDetailsApiModel'
import { mapCardholderRfiApiModel } from '@webapp/modules/spending-cards/data-access/mappers/mapCardholderRfiApiModel'
import { type GetCardholderDetailsResponse } from '@webapp/modules/spending-cards/data-access/types/GetCardholderDetailsResponse'
import { type CardholderConstructor } from '@webapp/modules/spending-cards/entities/Cardholder'
import { type CardholderAccountDetailsConstructor } from '@webapp/modules/spending-cards/entities/CardholderAccountDetails'
import { type CardholderRfiConstructor } from '@webapp/modules/spending-cards/entities/CardholderRfi'
import { mapCardholderApiModel } from '../../mappers/mapCardholderApiModel'
import { type CreateCardholderInputData } from '../../types/CreateCardholderInputData'
import { type CreateCardholderPayload } from '../../types/CreateCardholderPayload'
import { type GetCardholderResponse } from '../../types/GetCardholderResponse'
import { type GetCardholderRfiResponse } from '../../types/GetCardholderRfiResponse'
import { type GetCardholdersResponse } from '../../types/GetCardholdersResponse'
import { type PostSubmitCardholderRfiInputData } from '../../types/PostSubmitCardholderRfiInputData'
import { type PostSubmitCardholderRfiPayload } from '../../types/PostSubmitCardholderRfiPayload'
import { type UpdateCardholderDetailsInputData } from '../../types/UpdateCardholderDetailsInputData'
import { type UpdateCardholderDetailsPayload } from '../../types/UpdateCardholderDetailsPayload'

const PAY_API_URL = '/api/v3/pay/'

interface Dependency {
  httpClient: IHttpClient
}

export class CardholderRepository implements ICardholderRepository {
  constructor(private readonly deps: Dependency) {}

  async getCardholderDetails(): Promise<
    RepoStandardResponse<CardholderAccountDetailsConstructor>
  > {
    try {
      const res = await this.deps.httpClient.get<GetCardholderDetailsResponse>(
        `${PAY_API_URL}cardholders/me/`
      )

      const cardholder = res.data.data
      return {
        data: mapCardholderAccountDetailsApiModel(cardholder),
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getCardholderRfi(): Promise<
    RepoStandardResponse<CardholderRfiConstructor> | undefined
  > {
    try {
      const res = await this.deps.httpClient.get<GetCardholderRfiResponse>(
        `${PAY_API_URL}active-rfi/`
      )

      return { data: mapCardholderRfiApiModel(res.data.data) }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async submitCardholderRfi(
    rfiId: uuid,
    data: PostSubmitCardholderRfiInputData
  ): Promise<void> {
    const payload: PostSubmitCardholderRfiPayload = {
      identity: {
        attachments: {
          back_file_id: data.documentBackFileId,
          front_file_id: data.documentFrontFileId,
        },
        identity_document: {
          issuing_country: data.issuingCountry,
          number: data.documentNumber,
          type: data.documentType,
        },
      },
    }
    try {
      await this.deps.httpClient.post(
        `${PAY_API_URL}active-rfi/${rfiId}/respond/`,
        payload
      )
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async createCardholder(data: CreateCardholderInputData): Promise<void> {
    try {
      await this.deps.httpClient.post<CreateCardholderPayload>(
        `${PAY_API_URL}cardholders/`,
        {
          legal_first_name: data.firstName,
          legal_last_name: data.lastName,
          date_of_birth: data.dateOfBirth,
          mobile_number: data.phoneNumber,
          cardholder_email: data.email,
        }
      )
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async updateCardholderDetails(
    cardholderUuid: string,
    data: UpdateCardholderDetailsInputData
  ): Promise<void> {
    try {
      await this.deps.httpClient.put<UpdateCardholderDetailsPayload>(
        `${PAY_API_URL}cardholders/${cardholderUuid}/`,
        {
          legal_first_name: data.firstName,
          legal_last_name: data.lastName,
          date_of_birth: data.dateOfBirth,
          mobile_number: data.phoneNumber,
        }
      )
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getCardholders(
    params?: RepoFilterParams
  ): Promise<RepoPaginatedResponse<CardholderConstructor>> {
    try {
      const res = await this.deps.httpClient.get<GetCardholdersResponse>(
        '/api/v3/pay/cardholders/',
        { params: formatRepoFiltersToApiFilters(params) }
      )

      const data = res.data.data
      const metadata = res.data.metadata

      return {
        data: data.map((d) => mapCardholderApiModel(d)),
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 0,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getCardholderById(
    id: number
  ): Promise<RepoStandardResponse<CardholderConstructor>> {
    try {
      const res = await this.deps.httpClient.get<GetCardholderResponse>(
        `/api/v3/pay/cardholders/${id}/`
      )

      const cardholder = res.data.data

      return {
        data: mapCardholderApiModel(cardholder),
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }
}
